import React from 'react'
import {
  useGetList,
  TextField,
  ReferenceField,
  useDataProvider,
  useNotify,
  fetchStart,
  fetchEnd,
} from 'react-admin'
import { Link } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  TextField as MuiInput,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { DateTime } from 'luxon'
import { useDispatch } from 'react-redux'

const useAsideStyles = makeStyles((theme) => ({
  root: {
    width: 400,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}))

export const ParticipantAside = ({ record, basePath }) => {
  const classes = useAsideStyles()
  return (
    <div className={classes.root}>
      {record && <StatisticsCard record={record} />}
      {record && <EmailHistoryList record={record} />}
      {record && <EventList record={record} basePath={basePath} />}
      {record && <PaymentList record={record} />}
      {record && <DiscountList record={record} basePath={basePath} />}
    </div>
  )
}

const StatItem = ({ value, label }) => (
  <Grid item xs={6}>
    <Typography variant="h5" align="center">
      {value}
    </Typography>
    <Typography variant="body2" align="center" color="textSecondary">
      {label}
    </Typography>
  </Grid>
)

const StatisticsCard = ({ record }) => {
  const { total: bookingCount } = useGetList(
    'booking',
    { page: 1, perPage: 1 },
    { field: 'id', order: 'DESC' },
    { participant_id: record.id }
  )
  const { total: participationCount } = useGetList(
    'participant_date',
    { page: 1, perPage: 1 },
    { field: 'id', order: 'DESC' },
    { participantID: record.id }
  )
  const { total: paymentCount } = useGetList(
    'payment',
    { page: 1, perPage: 1 },
    { field: 'id', order: 'DESC' },
    { participantID: record.id }
  )
  const { data: logins, ids: loginIds } = useGetList(
    'log_user_logins',
    { page: 1, perPage: 1 },
    { field: 'datetime', order: 'DESC' },
    { participant_id: record.id }
  )
  const { total: appLoginCount } = useGetList(
    'log_user_logins',
    { page: 1, perPage: 1 },
    { field: 'datetime', order: 'DESC' },
    { participant_id: record.id, 'data.source': 'app' }
  )

  const { total: deviceCount } = useGetList(
    'participant_device',
    { page: 1, perPage: 1 },
    { field: 'id', order: 'DESC' },
    { participant_id: record.id }
  )

  const hasApp = appLoginCount > 0

  const lastLogin =
    loginIds.length > 0 && logins[loginIds[0]]
      ? DateTime.fromISO(logins[loginIds[0]].datetime).toFormat(
          'dd-MM-yyyy HH:mm'
        )
      : '-'

  const createdAt = record.created
    ? DateTime.fromISO(record.created).toFormat('dd-MM-yyyy')
    : '-'

  return (
    <Box m="0 0 1em 1em">
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Statistieken
          </Typography>
          <Grid container spacing={2}>
            <StatItem value={bookingCount ?? '-'} label="Boekingen" />
            <StatItem value={participationCount ?? '-'} label="Deelnames" />
            <StatItem value={paymentCount ?? '-'} label="Betalingen" />
            <StatItem value={lastLogin} label="Laatst ingelogd" />
            <StatItem value={hasApp ? 'Ja' : 'Nee'} label="App gedownload" />
            {hasApp && (
              <StatItem
                value={
                  record.notifications_settings?.notifications ? 'Aan' : 'Uit'
                }
                label="Push notificaties"
              />
            )}
            {hasApp && (
              <StatItem
                value={deviceCount > 0 ? 'Ja' : 'Nee'}
                label="FCM token"
              />
            )}
          </Grid>
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              Account aangemaakt: {createdAt}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

const EmailHistoryList = ({ record }) => {
  const { data: emails, ids: emailIds } = useGetList(
    'emailhistory',
    { page: 1, perPage: 10 },
    { field: 'sendDate', order: 'DESC' },
    { participantID: record.id }
  )
  return (
    <Box m="0 0 1em 1em">
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Laatste uitnodigingsmails
          </Typography>
          {emailIds.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Geen mails gevonden
            </Typography>
          )}
          {emailIds.length > 0 &&
            Object.values(emails).map((item, idx) => (
              <Box key={idx} mb={0.5}>
                <Box component="span" m="0 1em 0 0">
                  <Typography variant="body2" component="span">
                    {DateTime.fromISO(item.sendDate).toFormat(
                      'dd-MM-yyyy HH:mm'
                    )}
                  </Typography>
                </Box>
                <ReferenceField
                  source="eventID"
                  reference="event"
                  basePath="/event"
                  link="show"
                  record={item}>
                  <TextField source="name" />
                </ReferenceField>
              </Box>
            ))}
        </CardContent>
      </Card>
    </Box>
  )
}

const EventList = (props) => {
  const { data: dates, ids: dateIds } = useGetList(
    'participant_date',
    { page: 1, perPage: 10 },
    { field: 'participantID', order: 'DESC' },
    { participantID: props.record && props.record.id }
  )
  return (
    <>
      <Box m="0 0 1em 1em">
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Laatste 10 deelnames
            </Typography>
            {dateIds.length > 0 &&
              Object.values(dates).map((item, idx) => (
                <Box key={idx}>
                  <ReferenceField
                    source="dateID"
                    reference="date"
                    basePath="/date"
                    link="show"
                    record={item}>
                    <ReferenceField
                      source="eventID"
                      reference="event"
                      link={false}>
                      <TextField source="name" />
                    </ReferenceField>
                  </ReferenceField>
                </Box>
              ))}
          </CardContent>
        </Card>
      </Box>
    </>
  )
}

const PaymentList = ({ record }) => {
  const { data: payments, ids: paymentIds } = useGetList(
    'payment',
    { page: 1, perPage: 5 },
    { field: 'created', order: 'DESC' },
    { participantID: record.id }
  )
  return (
    <Box m="0 0 1em 1em">
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Laatste betalingen
          </Typography>
          {paymentIds.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Geen betalingen gevonden
            </Typography>
          )}
          {paymentIds.length > 0 &&
            paymentIds.map((id) => {
              const item = payments[id]
              if (!item) return null
              return (
                <Box
                  key={id}
                  mb={0.5}
                  display="flex"
                  justifyContent="space-between">
                  <Typography variant="body2">
                    {DateTime.fromISO(item.created).toFormat('dd-MM-yyyy')}
                  </Typography>
                  <Typography variant="body2">
                    {new Intl.NumberFormat('nl-NL', {
                      style: 'currency',
                      currency: 'EUR',
                    }).format(item.amount / 100)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item.status}
                  </Typography>
                </Box>
              )
            })}
          <Box mt={1}>
            <Button
              variant="text"
              color="primary"
              size="small"
              component={Link}
              to={{
                pathname: '/payment',
                search: `filter=${JSON.stringify({
                  participantID: record.id,
                })}`,
              }}>
              Alle betalingen bekijken
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

const DiscountList = (props) => {
  const dispatch = useDispatch()
  const dataProvider = useDataProvider()
  const notify = useNotify()

  const { data: rewards, ids: rewardIds } = useGetList(
    'reward',
    { page: 1, perPage: 0 },
    { field: 'participant_id', order: 'DESC' },
    { participant_id: props.record && props.record.id }
  )
  const [open, setOpen] = React.useState(false)
  const [amount, setAmount] = React.useState()
  const [loading, setLoading] = React.useState(false)

  const handleAmountChange = (event) => {
    setAmount(event.target.value)
  }

  const openDialog = () => {
    setOpen(true)
  }

  const closeDialog = () => {
    setOpen(false)
  }

  const addReward = () => {
    setLoading(true)
    dispatch(fetchStart())
    dataProvider
      .create(`reward/`, {
        data: {
          status: 'rewarded',
          type: 'amount',
          value: amount * 100,
          participant_id: props.record.id,
        },
      })
      .then(() => {
        notify(`Gelukt! Korting toegevoegd`)
      })
      .catch(() => {
        notify('Er ging iets fout...', 'warning')
      })
      .finally(() => {
        setLoading(false)
        dispatch(fetchEnd())
        setAmount('')
        setOpen(false)
      })
  }

  return (
    <>
      <Box m="0 0 1em 1em">
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Kortingen
            </Typography>
            {rewardIds.length > 0 &&
              Object.values(rewards).map((item, idx) => {
                return (
                  <Box key={idx}>
                    <Box component="span" m="0 1em 0 0">
                      {DateTime.fromISO(item.created).toFormat('dd-MM-yyyy')}
                    </Box>
                    <Box component="span" m="0 1em 0 0">
                      {new Intl.NumberFormat('nl-NL', {
                        style: 'currency',
                        currency: 'EUR',
                      }).format(item.value / 100)}
                    </Box>
                    <Box component="span" m="0 1em 0 0">
                      {item.status}
                    </Box>
                  </Box>
                )
              })}
            <Box m="1em 0 0 0">
              <Button
                variant="contained"
                color="primary"
                onClick={openDialog}
                disabled={loading}>
                Geef korting
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Dialog
        open={open}
        onClose={closeDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Geef korting</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Ken een korting toe aan deze deelnemer.
          </DialogContentText>
          <FormControl>
            <MuiInput
              value={amount}
              label="Euro"
              type="number"
              onChange={handleAmountChange}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="secondary">
            Annuleren
          </Button>

          <Button onClick={addReward} color="primary" autoFocus>
            Geef korting
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
